import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { HeroUserPillComponent } from './hero-user-pill.component';
import { PortalNavbarComponent } from './portal-navbar.component';
import { AuthService } from '../services/auth.service';
import { EventService, EventDto } from '../services/event.service';
import { OrganizationService } from '../services/organization.service';
import {
  GamePreferencesService,
  ArtifactAssetDto,
  EventTimerSettingDto
} from '../services/game-preferences.service';
import {
  EventThemeAssignmentDto,
  GameThemeCatalogService,
  GameThemeDto
} from '../services/game-theme-catalog.service';

type PrefSection    = 'sounds' | 'video' | 'themes' | 'timers';
type SoundCategoryId = 'right-answer' | 'wrong-answer' | 'end-of-round' | 'next-team-turn' | 'next-round' | 'intro' | 'break';

interface SoundOption   { id: string; label: string; }
interface SoundCategory { id: SoundCategoryId; label: string; description: string; multiSelect: boolean; options: SoundOption[]; }

type VideoCategoryId = 'right-answer' | 'wrong-answer' | 'end-of-round' | 'next-team-turn' | 'next-round' | 'intro' | 'break';
interface VideoOption   { id: string; label: string; }
interface VideoCategory { id: VideoCategoryId; label: string; description: string; multiSelect: boolean; options: VideoOption[]; }

type ThemePreviewRole = 'right-answer' | 'wrong-answer' | 'end-of-round' | 'next-round' | 'end-of-game' | 'next-team-turn' | 'buzzer' | 'intro' | 'break' | 'sudden-death' | 'three-three-and-out' | 'three-three-sudden-death' | 'head-to-head' | 'penalty-shot';

@Component({
  selector: 'app-game-preferences-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, PortalNavbarComponent, HeroUserPillComponent],
  template: `
    <div class="page">
      <app-portal-navbar></app-portal-navbar>

      <!-- ══════════════════ HERO ══════════════════════════════ -->
      <section class="page-hero">
        <div class="page-hero-inner">
          <a routerLink="/" class="home-btn">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                 viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            Home
          </a>
          <div class="hero-top-row">
            <div class="hero-icon hero-icon--violet">
              <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26"
                   viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                <line x1="4"  y1="21" x2="4"  y2="14"/>
                <line x1="4"  y1="10" x2="4"  y2="3"/>
                <line x1="12" y1="21" x2="12" y2="12"/>
                <line x1="12" y1="8"  x2="12" y2="3"/>
                <line x1="20" y1="21" x2="20" y2="16"/>
                <line x1="20" y1="12" x2="20" y2="3"/>
                <line x1="1"  y1="14" x2="7"  y2="14"/>
                <line x1="9"  y1="8"  x2="15" y2="8"/>
                <line x1="17" y1="16" x2="23" y2="16"/>
              </svg>
            </div>
            <app-hero-user-pill></app-hero-user-pill>
          </div>
          <h1 class="page-title">Game Preferences</h1>
          <p class="page-sub">Configure game rules, scoring, and behaviour settings.</p>
        </div>
      </section>

      <!-- ══════════════════ BODY ═══════════════════════════════ -->
      <section class="page-body">
        <div class="page-body-inner">

          <div class="pref-event-row">
            <label class="pref-event-label" for="pref-event-select">Event</label>
            <select id="pref-event-select" class="pref-event-select" [(ngModel)]="selectedEventId" (ngModelChange)="onEventChanged()">
              <option [ngValue]="0">Select event…</option>
              @for (evt of eventOptions; track evt.id) {
                <option [ngValue]="evt.id">{{ evt.name }}</option>
              }
            </select>
          </div>

          <!-- ── Section buttons ──────────────────────────────── -->
          <div class="pref-nav">
            <button class="pref-btn" [class.pref-btn--active]="activeSection() === 'themes'"
                    (click)="activeSection.set('themes')">
              <span class="pref-btn-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                     viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="13.5" cy="6.5" r="0.5" fill="currentColor"/>
                  <circle cx="17.5" cy="10.5" r="0.5" fill="currentColor"/>
                  <circle cx="8.5"  cy="7.5"  r="0.5" fill="currentColor"/>
                  <circle cx="6.5"  cy="12.5" r="0.5" fill="currentColor"/>
                  <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688
                           0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.042
                           a1.65 1.65 0 0 1 1.65-1.65h1.977C19.533 16.5 22 14.04 22 11
                           22 6.1 17.5 2 12 2z"/>
                </svg>
              </span>
              Themes
            </button>

            <button class="pref-btn" [class.pref-btn--active]="activeSection() === 'timers'"
                    (click)="activeSection.set('timers')">
              <span class="pref-btn-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                     viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="13" r="8"/>
                  <polyline points="12 9 12 13 15 15"/>
                </svg>
              </span>
              Timers
            </button>

          </div>

          <!-- ── Section content panel ────────────────────────── -->
          <div class="pref-panel">

            @switch (activeSection()) {

              @case ('sounds') {
                <div class="pref-panel-hd">
                  <span class="pref-panel-title">Sounds</span>
                  <span class="pref-panel-sub">Audio cues and music settings.</span>
                </div>
                <div class="pref-panel-body">
                  <div class="sounds-layout">

                    <!-- Col 1 – category list -->
                    <div class="sound-cat-list">
                      @for (cat of soundCategories; track cat.id) {
                        <div class="sound-cat-card"
                             [class.sound-cat-card--active]="selectedSoundCategory() === cat.id"
                             (click)="selectedSoundCategory.set(cat.id)">
                          <div class="sound-cat-info">
                            <span class="sound-cat-label">{{ cat.label }}</span>
                            <span class="sound-cat-desc">{{ cat.description }}</span>
                          </div>
                          @if (cat.multiSelect && getSelectedCount(cat.id) > 0) {
                            <span class="sound-sel-badge">{{ getSelectedCount(cat.id) }}</span>
                          }
                          <div class="sound-cat-chevron">
                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13"
                                 viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                 stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                              <polyline points="9 18 15 12 9 6"/>
                            </svg>
                          </div>
                        </div>
                      }
                    </div>

                    <!-- Col 2 – sound options -->
                    <div class="sound-picker">
                      <p class="sound-picker-hd">
                        Choose one sound for <strong>{{ activeSoundCategory.label }}</strong>
                      </p>
                      <div class="artifact-grid">
                        @for (opt of getSoundOptions(activeSoundCategory); track opt.id) {
                          <div class="artifact-tile">
                            <button class="artifact-card"
                                 type="button"
                                 [class.artifact-card--active]="isSoundSelected(selectedSoundCategory(), opt.id)"
                                 (click)="toggleSound(selectedSoundCategory(), opt.id)">
                              @if (isSoundSelected(selectedSoundCategory(), opt.id)) {
                                <div class="artifact-card-check">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="9" height="9"
                                       viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                       stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round">
                                    <polyline points="20 6 9 17 4 12"/>
                                  </svg>
                                </div>
                              }
                              <div class="artifact-card-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                     viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                     stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                  <path d="M9 18V5l12-2v13"/>
                                  <circle cx="6" cy="18" r="3"/>
                                  <circle cx="18" cy="16" r="3"/>
                                </svg>
                              </div>
                              <span class="artifact-card-name">{{ opt.label }}</span>
                              <span class="artifact-card-meta">{{ formatAssetSize(getOptionAsset(opt.id)?.sizeBytes ?? 0) }}</span>
                              @if (isOptionDefault(opt.id)) {
                                <span class="artifact-card-default">Default</span>
                              }
                            </button>
                            <button class="asset-toggle-btn"
                                    type="button"
                                    (click)="toggleOptionPreview(opt.id, $event)">
                              {{ isOptionPlaying(opt.id) ? 'Hide' : 'Play' }}
                            </button>
                            @if (isOptionPlaying(opt.id) && (previewUrl(opt.id) || previewErrorMessage())) {
                              <div class="artifact-preview">
                                @if (previewUrl(opt.id)) {
                                  <audio controls [src]="previewUrl(opt.id)" (loadeddata)="clearMediaDecodeError()" (error)="onOptionMediaDecodeError($event, opt.id)"></audio>
                                }
                                @if (previewErrorMessage()) {
                                  <div class="preview-error">{{ previewErrorMessage() }}</div>
                                }
                              </div>
                            }
                          </div>
                        } @empty {
                          <div class="pref-panel-placeholder">
                            <p>No sound artifacts are available for this category.</p>
                          </div>
                        }
                      </div>
                    </div>

                  </div>
                </div>
              }

              @case ('video') {
                <div class="pref-panel-hd">
                  <span class="pref-panel-title">Video</span>
                  <span class="pref-panel-sub">Display and video output settings.</span>
                </div>
                <div class="pref-panel-body">
                  <div class="sounds-layout">

                    <!-- Col 1 – category list -->
                    <div class="sound-cat-list">
                      @for (cat of videoCategories; track cat.id) {
                        <div class="sound-cat-card"
                             [class.sound-cat-card--active]="selectedVideoCategory() === cat.id"
                             (click)="selectedVideoCategory.set(cat.id)">
                          <div class="sound-cat-info">
                            <span class="sound-cat-label">{{ cat.label }}</span>
                            <span class="sound-cat-desc">{{ cat.description }}</span>
                          </div>
                          <div class="sound-cat-chevron">
                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13"
                                 viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                 stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                              <polyline points="9 18 15 12 9 6"/>
                            </svg>
                          </div>
                        </div>
                      }
                    </div>

                    <!-- Col 2 – video options -->
                    <div class="sound-picker">
                      <p class="sound-picker-hd">
                        Choose one video for <strong>{{ activeVideoCategory.label }}</strong>
                      </p>
                      <div class="artifact-grid">
                        @for (opt of getVideoOptions(activeVideoCategory); track opt.id) {
                          <div class="artifact-tile">
                            <button class="artifact-card"
                                 type="button"
                                 [class.artifact-card--active]="isVideoSelected(selectedVideoCategory(), opt.id)"
                                 (click)="toggleVideo(selectedVideoCategory(), opt.id)">
                              @if (isVideoSelected(selectedVideoCategory(), opt.id)) {
                                <div class="artifact-card-check">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="9" height="9"
                                       viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                       stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round">
                                    <polyline points="20 6 9 17 4 12"/>
                                  </svg>
                                </div>
                              }
                              <div class="artifact-card-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                     viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                     stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                  <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/>
                                  <line x1="7"  y1="2"  x2="7"  y2="22"/>
                                  <line x1="17" y1="2"  x2="17" y2="22"/>
                                  <line x1="2"  y1="12" x2="22" y2="12"/>
                                  <line x1="2"  y1="7"  x2="7"  y1="7"/>
                                  <line x1="17" y1="7"  x2="22" y2="7"/>
                                  <line x1="17" y1="17" x2="22" y2="17"/>
                                  <line x1="2"  y1="17" x2="7"  y2="17"/>
                                </svg>
                              </div>
                              <span class="artifact-card-name">{{ opt.label }}</span>
                              <span class="artifact-card-meta">{{ formatAssetSize(getOptionAsset(opt.id)?.sizeBytes ?? 0) }}</span>
                              @if (isOptionDefault(opt.id)) {
                                <span class="artifact-card-default">Default</span>
                              }
                            </button>
                            <button class="asset-toggle-btn"
                                    type="button"
                                    (click)="toggleOptionPreview(opt.id, $event)">
                              {{ isOptionPlaying(opt.id) ? 'Hide' : 'Play' }}
                            </button>
                            @if (isOptionPlaying(opt.id) && (previewUrl(opt.id) || previewErrorMessage())) {
                              <div class="artifact-preview">
                                @if (previewUrl(opt.id)) {
                                  <video controls class="preview-video" [src]="previewUrl(opt.id)" (loadeddata)="clearMediaDecodeError()" (error)="onOptionMediaDecodeError($event, opt.id)"></video>
                                }
                                @if (previewErrorMessage()) {
                                  <div class="preview-error">{{ previewErrorMessage() }}</div>
                                }
                              </div>
                            }
                          </div>
                        } @empty {
                          <div class="pref-panel-placeholder">
                            <p>No video artifacts are available for this category.</p>
                          </div>
                        }
                      </div>
                    </div>

                  </div>
                </div>
              }

              @case ('themes') {
                <div class="pref-panel-hd">
                  <span class="pref-panel-title">Themes</span>
                  <span class="pref-panel-sub">Colour schemes and visual style options.</span>
                </div>
                <div class="pref-panel-body">
                  <div class="themes-layout">
                    <aside class="themes-left-col">
                      <div class="themes-filter-section">
                        <label class="field">
                          <span>Search Themes</span>
                          <input type="text" [(ngModel)]="themeSearchText" placeholder="Search by name, key, description..." />
                        </label>
                        <label class="field">
                          <span>Category</span>
                          <select [(ngModel)]="selectedThemeCategory">
                            <option value="all">All Categories</option>
                            @for (category of themeCategoryOptions; track category) {
                              <option [value]="category">{{ category }}</option>
                            }
                          </select>
                        </label>
                      </div>

                      <div class="themes-tree-section">
                        @if (filteredThemeGroups.length === 0) {
                          <div class="pref-panel-placeholder">
                            <p>No themes match the current filters.</p>
                          </div>
                        }

                        @for (group of filteredThemeGroups; track group.category) {
                          <div class="theme-tree-group">
                            <div class="theme-tree-group-label">{{ group.category }}</div>
                            <div class="theme-tree-list">
                              @for (theme of group.themes; track theme.id) {
                                <button
                                  class="theme-tree-item"
                                  type="button"
                                  [class.theme-tree-item--active]="selectedThemeId() === theme.id"
                                  (click)="selectTheme(theme.id)">
                                  <span class="theme-tree-item-name">{{ theme.name }}</span>
                                  <span class="theme-tree-item-key">{{ theme.themeKey }}</span>
                                </button>
                              }
                            </div>
                          </div>
                        }
                      </div>

                      <div class="themes-save-section">
                        <button
                          class="btn btn--primary"
                          type="button"
                          [disabled]="!selectedThemeId() || !selectedEventId || isSavingThemeAssignment()"
                          (click)="saveSelectedThemeAssignment()">
                          {{ isSavingThemeAssignment() ? 'Saving...' : 'Save Theme Selection' }}
                        </button>
                        @if (themeAssignmentMessage()) {
                          <div class="theme-assignment-message">{{ themeAssignmentMessage() }}</div>
                        }
                        @if (activeThemeAssignment() && selectedEventId) {
                          <div class="theme-assignment-current">
                            Assigned Theme Id: {{ activeThemeAssignment()?.gameThemeId }}
                          </div>
                        }
                      </div>
                    </aside>

                    <section class="themes-right-col">
                      <div class="theme-preview-card">
                        <div class="theme-preview-head">
                          <div>
                            <div class="theme-preview-title">{{ selectedThemePreview?.name || 'Theme Preview' }}</div>
                            <div class="theme-preview-sub">{{ selectedThemePreview?.category || 'Select a theme' }} · {{ selectedThemePreview?.themeKey || 'No theme selected' }}</div>
                          </div>
                        </div>
                        <div class="theme-preview-description">{{ selectedThemePreview?.description || 'Select a theme to preview details.' }}</div>
                        <div class="theme-preview-layout-placeholder">
                          <div class="theme-preview-pane">
                            @if (themeBackgroundPreviewUrl(1)) {
                              <img class="theme-preview-background-image" [src]="themeBackgroundPreviewUrl(1)" alt="Theme background 1" />
                            } @else {
                              <div class="theme-preview-pane-empty">Background 1</div>
                            }
                          </div>
                          <div class="theme-preview-media-lines">
                            <div class="theme-preview-media-line theme-preview-media-line--stack">
                              <span>Right Answer</span>
                              <div class="theme-preview-play-row">
                                @for (asset of getThemePreviewAssets('right-answer', 3); track asset.id + '-' + $index) {
                                  <button class="theme-play-btn" type="button" (click)="toggleThemeAssetPreview(asset.id, $event)">
                                    {{ isThemeAssetPlaying(asset.id) ? 'Pause' : 'Play' }}
                                  </button>
                                } @empty {
                                  <span class="theme-preview-empty">No media</span>
                                }
                              </div>
                            </div>

                            <div class="theme-preview-media-line theme-preview-media-line--stack">
                              <span>Wrong Answer</span>
                              <div class="theme-preview-play-row">
                                @for (asset of getThemePreviewAssets('wrong-answer', 3); track asset.id + '-' + $index) {
                                  <button class="theme-play-btn" type="button" (click)="toggleThemeAssetPreview(asset.id, $event)">
                                    {{ isThemeAssetPlaying(asset.id) ? 'Pause' : 'Play' }}
                                  </button>
                                } @empty {
                                  <span class="theme-preview-empty">No media</span>
                                }
                              </div>
                            </div>

                            <div class="theme-preview-media-line theme-preview-media-line--split">
                              <div class="theme-preview-media-cell">
                                <span>End of Round</span>
                                <div class="theme-preview-play-row">
                                  @for (asset of getThemePreviewAssets('end-of-round', 1); track asset.id + '-' + $index) {
                                    <button class="theme-play-btn" type="button" (click)="toggleThemeAssetPreview(asset.id, $event)">
                                      {{ isThemeAssetPlaying(asset.id) ? 'Pause' : 'Play' }}
                                    </button>
                                  } @empty {
                                    <span class="theme-preview-empty">No media</span>
                                  }
                                </div>
                              </div>
                              <div class="theme-preview-media-cell">
                                <span>Next Round</span>
                                <div class="theme-preview-play-row">
                                  @for (asset of getThemePreviewAssets('next-round', 1); track asset.id + '-' + $index) {
                                    <button class="theme-play-btn" type="button" (click)="toggleThemeAssetPreview(asset.id, $event)">
                                      {{ isThemeAssetPlaying(asset.id) ? 'Pause' : 'Play' }}
                                    </button>
                                  } @empty {
                                    <span class="theme-preview-empty">No media</span>
                                  }
                                </div>
                              </div>
                              <div class="theme-preview-media-cell">
                                <span>End of Game</span>
                                <div class="theme-preview-play-row">
                                  @for (asset of getThemePreviewAssets('end-of-game', 1); track asset.id + '-' + $index) {
                                    <button class="theme-play-btn" type="button" (click)="toggleThemeAssetPreview(asset.id, $event)">
                                      {{ isThemeAssetPlaying(asset.id) ? 'Pause' : 'Play' }}
                                    </button>
                                  } @empty {
                                    <span class="theme-preview-empty">No media</span>
                                  }
                                </div>
                              </div>
                            </div>

                            <div class="theme-preview-media-line theme-preview-media-line--split">
                              <div class="theme-preview-media-cell">
                                <span>Next Team Turn</span>
                                <div class="theme-preview-play-row">
                                  @for (asset of getThemePreviewAssets('next-team-turn', 1); track asset.id + '-' + $index) {
                                    <button class="theme-play-btn" type="button" (click)="toggleThemeAssetPreview(asset.id, $event)">
                                      {{ isThemeAssetPlaying(asset.id) ? 'Pause' : 'Play' }}
                                    </button>
                                  } @empty {
                                    <span class="theme-preview-empty">No media</span>
                                  }
                                </div>
                              </div>
                              <div class="theme-preview-media-cell">
                                <span>Buzzer</span>
                                <div class="theme-preview-play-row">
                                  @for (asset of getThemePreviewAssets('buzzer', 1); track asset.id + '-' + $index) {
                                    <button class="theme-play-btn theme-play-btn--audio" type="button" (click)="toggleThemeAssetPreview(asset.id, $event)">
                                      {{ isThemeAssetPlaying(asset.id) ? 'Pause' : 'Play' }}
                                    </button>
                                  } @empty {
                                    <span class="theme-preview-empty">No media</span>
                                  }
                                </div>
                              </div>
                              <div class="theme-preview-media-cell">
                                <span>Sudden Death</span>
                                <div class="theme-preview-play-row">
                                  @for (asset of getThemePreviewAssets('sudden-death', 1); track asset.id + '-' + $index) {
                                    <button class="theme-play-btn" type="button" (click)="toggleThemeAssetPreview(asset.id, $event)">
                                      {{ isThemeAssetPlaying(asset.id) ? 'Pause' : 'Play' }}
                                    </button>
                                  } @empty {
                                    <span class="theme-preview-empty">No media</span>
                                  }
                                </div>
                              </div>
                            </div>

                            <div class="theme-preview-media-line theme-preview-media-line--stack">
                              <span>Game Flow Modes</span>
                              <div class="theme-preview-play-row">
                                @for (asset of getThemePreviewAssets('three-three-and-out', 1); track asset.id + '-' + $index) {
                                  <button class="theme-play-btn" type="button" (click)="toggleThemeAssetPreview(asset.id, $event)">
                                    {{ isThemeAssetPlaying(asset.id) ? 'Pause' : 'Play' }}
                                  </button>
                                }
                                @for (asset of getThemePreviewAssets('three-three-sudden-death', 1); track asset.id + '-' + $index) {
                                  <button class="theme-play-btn" type="button" (click)="toggleThemeAssetPreview(asset.id, $event)">
                                    {{ isThemeAssetPlaying(asset.id) ? 'Pause' : 'Play' }}
                                  </button>
                                }
                                @for (asset of getThemePreviewAssets('head-to-head', 1); track asset.id + '-' + $index) {
                                  <button class="theme-play-btn" type="button" (click)="toggleThemeAssetPreview(asset.id, $event)">
                                    {{ isThemeAssetPlaying(asset.id) ? 'Pause' : 'Play' }}
                                  </button>
                                }
                                @for (asset of getThemePreviewAssets('penalty-shot', 1); track asset.id + '-' + $index) {
                                  <button class="theme-play-btn" type="button" (click)="toggleThemeAssetPreview(asset.id, $event)">
                                    {{ isThemeAssetPlaying(asset.id) ? 'Pause' : 'Play' }}
                                  </button>
                                }
                              </div>
                            </div>

                            <div class="theme-preview-media-line theme-preview-media-line--split">
                              <div class="theme-preview-media-cell">
                                <span>Intro</span>
                                <div class="theme-preview-play-row">
                                  @for (asset of getThemePreviewAssets('intro', 1); track asset.id + '-' + $index) {
                                    <button class="theme-play-btn" type="button" (click)="toggleThemeAssetPreview(asset.id, $event)">
                                      {{ isThemeAssetPlaying(asset.id) ? 'Pause' : 'Play' }}
                                    </button>
                                  } @empty {
                                    <span class="theme-preview-empty">No media</span>
                                  }
                                </div>
                              </div>
                              <div class="theme-preview-media-cell">
                                <span>Break</span>
                                <div class="theme-preview-play-row">
                                  @for (asset of getThemePreviewAssets('break', 1); track asset.id + '-' + $index) {
                                    <button class="theme-play-btn" type="button" (click)="toggleThemeAssetPreview(asset.id, $event)">
                                      {{ isThemeAssetPlaying(asset.id) ? 'Pause' : 'Play' }}
                                    </button>
                                  } @empty {
                                    <span class="theme-preview-empty">No media</span>
                                  }
                                </div>
                              </div>
                            </div>

                            @if (activePreviewAssetId() && previewUrlMapHas(activePreviewAssetId()!)) {
                              <div class="artifact-preview">
                                <video controls class="preview-video" [src]="previewUrlByAssetId(activePreviewAssetId()!)" (loadeddata)="clearMediaDecodeError()" (error)="onThemeMediaDecodeError($event)"></video>
                                @if (previewErrorMessage()) {
                                  <div class="preview-error">{{ previewErrorMessage() }}</div>
                                }
                              </div>
                            }
                          </div>
                        </div>

                        <div class="theme-preview-projector">
                          <div class="theme-preview-projector-title">Projector</div>
                          <div class="theme-preview-projector-placeholder">
                            @if (themeBackgroundPreviewUrl(2)) {
                              <img class="theme-preview-projector-image" [src]="themeBackgroundPreviewUrl(2)" alt="Theme background 2" />
                            } @else {
                              <span>Background 2</span>
                            }
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>
                </div>
              }

              @case ('timers') {
                <div class="pref-panel-hd">
                  <span class="pref-panel-title">Timers</span>
                  <span class="pref-panel-sub">Configure event-level podium and answer timer values.</span>
                </div>
                <div class="pref-panel-body">
                  <div class="timers-layout">
                    <div class="timers-row">
                      <label class="timers-toggle">
                        <input type="checkbox" [(ngModel)]="podiumTimerEnabled" />
                        <span>Podium Timer</span>
                      </label>
                      <input
                        class="form-control timers-input"
                        type="number"
                        min="1"
                        [(ngModel)]="podiumTimerSeconds"
                        [disabled]="!podiumTimerEnabled" />
                      <span class="timers-unit">seconds</span>
                    </div>

                    <div class="timers-row">
                      <label class="timers-toggle">
                        <input type="checkbox" [(ngModel)]="answerTimerEnabled" />
                        <span>Answer Timer</span>
                      </label>
                      <input
                        class="form-control timers-input"
                        type="number"
                        min="1"
                        [(ngModel)]="answerTimerSeconds"
                        [disabled]="!answerTimerEnabled" />
                      <span class="timers-unit">seconds</span>
                    </div>

                    <div class="timers-save-section">
                      <button class="btn btn--primary" type="button" [disabled]="!selectedEventId || isSavingTimerSettings()" (click)="saveTimerSettings()">
                        {{ isSavingTimerSettings() ? 'Saving...' : 'Save Timer Settings' }}
                      </button>
                      @if (timerSettingsMessage()) {
                        <div class="theme-assignment-message">{{ timerSettingsMessage() }}</div>
                      }
                    </div>
                  </div>
                </div>
              }

            }

          </div>

        </div>
      </section>
    </div>
  `,
  styles: [`
    .page { display: flex; flex-direction: column; min-height: 100vh; }

    /* ── Hero ─────────────────────────────────────────── */
    .page-hero {
      background:
        radial-gradient(
          ellipse at 12% 60%,
          color-mix(in srgb, #8b5cf6 8%, transparent) 0%,
          transparent 55%
        ),
        linear-gradient(155deg, var(--color-bg-start) 0%, var(--color-bg-end) 100%);
      border-bottom: 1px solid var(--color-border);
      padding: 3rem 1.5rem 3.5rem;
    }

    .page-hero-inner {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      margin: 0 auto;
      max-width: 1280px;
    }

    .home-btn {
      align-items: center;
      align-self: flex-start;
      background: color-mix(in srgb, var(--color-surface-2) 65%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 0.5rem;
      color: var(--color-muted);
      display: flex;
      font-size: 0.8rem;
      font-weight: 500;
      gap: 0.35rem;
      padding: 0.32rem 0.7rem;
      text-decoration: none;
      transition: background 0.15s, color 0.15s;
    }
    .home-btn:hover {
      background: color-mix(in srgb, var(--color-surface-2) 90%, transparent);
      color: var(--color-text);
    }

    .hero-top-row {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      gap: 0.85rem;
    }

    .hero-icon {
      align-items: center;
      border-radius: 0.75rem;
      display: flex;
      height: 52px;
      justify-content: center;
      width: 52px;
    }
    .hero-icon--violet { background: color-mix(in srgb, #8b5cf6 16%, transparent); color: #a78bfa; }

    .page-title {
      color: var(--color-text);
      font-size: clamp(1.6rem, 3vw, 2.2rem);
      font-weight: 800;
      line-height: 1.15;
      margin: 0;
    }

    .page-sub {
      color: var(--color-muted);
      font-size: 0.93rem;
      line-height: 1.6;
      margin: 0;
      max-width: 46ch;
    }

    /* ── Body ─────────────────────────────────────────── */
    .page-body { flex: 1; padding: 2.5rem 1.5rem; }

    .page-body-inner {
      display: flex;
      flex-direction: column;
      gap: 1.25rem;
      margin: 0 auto;
      max-width: 1280px;
    }

    .pref-event-row {
      align-items: center;
      display: flex;
      gap: 0.6rem;
    }

    .pref-event-label {
      color: var(--color-muted);
      font-size: 0.78rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .pref-event-select {
      appearance: auto;
      background: #1e293b;
      border: 1px solid #334155;
      border-radius: 0.45rem;
      color: #f8fafc;
      font-size: 0.8rem;
      min-width: 16rem;
      outline: none;
      padding: 0.35rem 0.6rem;
    }
    .pref-event-select:focus { border-color: #8b5cf6; }

    /* ── Section nav buttons ──────────────────────────── */
    .pref-nav {
      display: flex;
      flex-wrap: wrap;
      gap: 0.75rem;
    }

    .pref-btn {
      align-items: center;
      background: color-mix(in srgb, var(--color-surface-2) 75%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 0.6rem;
      color: var(--color-muted);
      cursor: pointer;
      display: flex;
      font-size: 0.85rem;
      font-weight: 600;
      gap: 0.5rem;
      padding: 0.6rem 1.15rem;
      transition: background 0.15s, border-color 0.15s, color 0.15s;
    }
    .pref-btn:hover {
      background: color-mix(in srgb, #8b5cf6 10%, transparent);
      border-color: color-mix(in srgb, #8b5cf6 35%, var(--color-border));
      color: #a78bfa;
    }
    .pref-btn--active {
      background: color-mix(in srgb, #8b5cf6 14%, transparent);
      border-color: color-mix(in srgb, #8b5cf6 50%, var(--color-border));
      color: #a78bfa;
    }
    .pref-btn-icon { align-items: center; display: flex; }

    .pref-migration-banner {
      background: color-mix(in srgb, #06b6d4 10%, var(--color-surface-2));
      border: 1px solid color-mix(in srgb, #06b6d4 36%, var(--color-border));
      border-radius: 0.75rem;
      display: grid;
      gap: 0.35rem;
      padding: 0.85rem 1rem;
    }

    .pref-migration-title {
      color: var(--color-text);
      font-size: 0.82rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    .pref-migration-banner p {
      color: var(--color-muted);
      font-size: 0.82rem;
      margin: 0;
    }

    .pref-migration-link {
      color: #67e8f9;
      font-weight: 600;
      text-decoration: none;
    }

    .pref-migration-link:hover {
      text-decoration: underline;
    }

    /* ── Content panel shell ──────────────────────────── */
    .pref-panel {
      background: color-mix(in srgb, var(--color-surface-2) 75%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 1rem;
      overflow: hidden;
    }

    .pref-panel-hd {
      align-items: baseline;
      border-bottom: 1px solid var(--color-border);
      display: flex;
      gap: 0.75rem;
      padding: 0.85rem 1.25rem;
    }
    .pref-panel-title { color: var(--color-text); font-size: 0.88rem; font-weight: 700; }
    .pref-panel-sub   { color: var(--color-muted); font-size: 0.75rem; }

    .pref-panel-body { padding: 1.25rem; }

    .pref-panel-placeholder {
      align-items: center;
      color: color-mix(in srgb, var(--color-muted) 45%, transparent);
      display: flex;
      flex-direction: column;
      gap: 0.9rem;
      justify-content: center;
      min-height: 220px;
      text-align: center;
    }
    .pref-panel-placeholder p {
      color: color-mix(in srgb, var(--color-muted) 70%, transparent);
      font-size: 0.83rem;
      line-height: 1.65;
      margin: 0;
    }

    /* ── Sounds two-column layout ─────────────────────── */
    .sounds-layout {
      display: grid;
      gap: 1.25rem;
      grid-template-columns: 240px 1fr;
    }

    /* ── Col 1 – category cards ───────────────────────── */
    .sound-cat-list {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .sound-cat-card {
      align-items: center;
      background: color-mix(in srgb, var(--color-surface-2) 60%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 0.65rem;
      cursor: pointer;
      display: flex;
      gap: 0.65rem;
      padding: 0.75rem 0.9rem;
      transition: background 0.15s, border-color 0.15s;
    }
    .sound-cat-card:hover {
      background: color-mix(in srgb, #8b5cf6 8%, transparent);
      border-color: color-mix(in srgb, #8b5cf6 28%, var(--color-border));
    }
    .sound-cat-card--active {
      background: color-mix(in srgb, #8b5cf6 13%, transparent);
      border-color: color-mix(in srgb, #8b5cf6 50%, var(--color-border));
      border-left: 3px solid #8b5cf6;
      padding-left: calc(0.9rem - 2px);
    }

    .sound-cat-info {
      display: flex;
      flex: 1;
      flex-direction: column;
      gap: 0.15rem;
      min-width: 0;
    }
    .sound-cat-label { color: var(--color-text); font-size: 0.83rem; font-weight: 600; }
    .sound-cat-desc  { color: var(--color-muted); font-size: 0.7rem; line-height: 1.4; }

    .sound-sel-badge {
      background: color-mix(in srgb, #8b5cf6 18%, transparent);
      border-radius: 9999px;
      color: #a78bfa;
      flex-shrink: 0;
      font-size: 0.65rem;
      font-weight: 700;
      padding: 0.12rem 0.5rem;
    }

    .sound-cat-chevron {
      color: var(--color-muted);
      flex-shrink: 0;
      opacity: 0.35;
      transition: opacity 0.15s;
    }
    .sound-cat-card:hover .sound-cat-chevron,
    .sound-cat-card--active .sound-cat-chevron { opacity: 0.85; }

    /* ── Col 2 – sound picker ─────────────────────────── */
    .sound-picker {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .asset-upload-row {
      align-items: center;
      display: flex;
      gap: 0.5rem;
    }

    .asset-upload-input {
      color: var(--color-muted);
      font-size: 0.72rem;
    }

    .asset-upload-btn {
      background: color-mix(in srgb, #8b5cf6 22%, transparent);
      border: 1px solid color-mix(in srgb, #8b5cf6 48%, var(--color-border));
      border-radius: 0.45rem;
      color: #c4b5fd;
      cursor: pointer;
      font-size: 0.72rem;
      font-weight: 600;
      padding: 0.3rem 0.65rem;
    }

    .asset-toggle-btn {
      background: color-mix(in srgb, var(--color-surface-2) 70%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 0.4rem;
      color: var(--color-muted);
      cursor: pointer;
      font-size: 0.66rem;
      font-weight: 600;
      margin-top: 0.2rem;
      padding: 0.2rem 0.45rem;
    }

    .preview-video {
      border: 1px solid var(--color-border);
      border-radius: 0.6rem;
      max-height: 220px;
      width: min(100%, 360px);
    }
    .artifact-grid {
      display: grid;
      gap: 0.75rem;
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .artifact-tile {
      display: flex;
      flex-direction: column;
      gap: 0.35rem;
    }

    .artifact-card {
      align-items: center;
      background: color-mix(in srgb, var(--color-surface-2) 60%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 0.85rem;
      color: inherit;
      cursor: pointer;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      padding: 1rem;
      position: relative;
      text-align: center;
    }

    .artifact-card--active {
      background: color-mix(in srgb, #8b5cf6 13%, transparent);
      border-color: color-mix(in srgb, #8b5cf6 55%, var(--color-border));
    }

    .artifact-card-check {
      align-items: center;
      background: #8b5cf6;
      border-radius: 50%;
      color: #fff;
      display: flex;
      height: 18px;
      justify-content: center;
      position: absolute;
      right: 10px;
      top: 10px;
      width: 18px;
    }

    .artifact-card-icon { color: #a78bfa; }
    .artifact-card-name {
      color: var(--color-text);
      font-size: 0.82rem;
      font-weight: 600;
      word-break: break-word;
    }
    .artifact-card-meta {
      color: var(--color-muted);
      font-size: 0.7rem;
    }
    .artifact-card-default {
      background: color-mix(in srgb, #8b5cf6 16%, transparent);
      border-radius: 9999px;
      color: #c4b5fd;
      font-size: 0.62rem;
      font-weight: 700;
      padding: 0.14rem 0.55rem;
    }

    .artifact-preview {
      display: flex;
      flex-direction: column;
      gap: 0.45rem;
    }

    .artifact-preview audio {
      width: 100%;
    }

    .preview-error {
      border: 1px solid color-mix(in srgb, #ef4444 45%, transparent);
      background: color-mix(in srgb, #ef4444 10%, transparent);
      color: #fecaca;
      border-radius: .5rem;
      padding: .45rem .6rem;
      font-size: .78rem;
    }

    /* ── Responsive ───────────────────────────────────── */
    @media (max-width: 700px) {
      .sounds-layout  { grid-template-columns: 1fr; }
      .artifact-grid  { grid-template-columns: repeat(2, minmax(0, 1fr)); }
      .themes-layout  { grid-template-columns: 1fr; }
    }
    @media (max-width: 520px) {
      .pref-nav { gap: 0.5rem; }
      .pref-btn { font-size: 0.78rem; padding: 0.5rem 0.85rem; }
    }
    .themes-layout {
      display: grid;
      gap: 1rem;
      grid-template-columns: minmax(260px, 25%) minmax(0, 75%);
    }

    .themes-left-col,
    .themes-right-col {
      background: color-mix(in srgb, var(--color-surface-2) 38%, transparent);
      border: 1px solid var(--color-border);
      border-radius: .85rem;
      display: flex;
      flex-direction: column;
      gap: .85rem;
      padding: .9rem;
    }

    .themes-filter-section {
      display: grid;
      gap: .6rem;
    }

    .themes-tree-section {
      display: flex;
      flex-direction: column;
      gap: .65rem;
      max-height: 460px;
      overflow: auto;
      padding-right: .2rem;
    }

    .theme-tree-group {
      display: flex;
      flex-direction: column;
      gap: .45rem;
    }

    .theme-tree-group-label {
      color: var(--color-muted);
      font-size: .72rem;
      font-weight: 700;
      letter-spacing: .04em;
      text-transform: uppercase;
    }

    .theme-tree-list {
      display: flex;
      flex-direction: column;
      gap: .4rem;
    }

    .theme-tree-item {
      align-items: flex-start;
      background: color-mix(in srgb, var(--color-surface-2) 65%, transparent);
      border: 1px solid var(--color-border);
      border-radius: .6rem;
      color: var(--color-text);
      cursor: pointer;
      display: flex;
      flex-direction: column;
      font-size: .82rem;
      gap: .2rem;
      padding: .52rem .62rem;
      text-align: left;
      transition: border-color .15s, background .15s;
      width: 100%;
    }

    .theme-tree-item:hover {
      border-color: color-mix(in srgb, #8b5cf6 28%, var(--color-border));
    }

    .theme-tree-item--active {
      background: color-mix(in srgb, #8b5cf6 13%, transparent);
      border-color: color-mix(in srgb, #8b5cf6 50%, var(--color-border));
    }

    .theme-tree-item-name {
      font-weight: 600;
    }

    .theme-tree-item-key {
      color: var(--color-muted);
      font-size: .68rem;
    }

    .themes-save-section {
      margin-top: auto;
    }

    .themes-save-section .btn {
      width: 100%;
    }

    .timers-layout {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      max-width: 560px;
    }

    .timers-row {
      align-items: center;
      display: grid;
      gap: 0.75rem;
      grid-template-columns: minmax(180px, 1fr) 120px auto;
    }

    .timers-toggle {
      align-items: center;
      color: var(--color-text);
      display: inline-flex;
      font-size: 0.86rem;
      font-weight: 600;
      gap: 0.5rem;
    }

    .timers-input {
      text-align: right;
    }

    .timers-unit {
      color: var(--color-muted);
      font-size: 0.78rem;
    }

    .timers-save-section {
      margin-top: 0.5rem;
    }

    .theme-preview-card {
      display: flex;
      flex-direction: column;
      gap: .8rem;
    }

    .theme-preview-head {
      align-items: center;
      display: flex;
      justify-content: space-between;
    }

    .theme-preview-title {
      color: var(--color-text);
      font-size: .95rem;
      font-weight: 700;
    }

    .theme-preview-sub,
    .theme-preview-description {
      color: var(--color-muted);
      font-size: .78rem;
    }

    .theme-preview-layout-placeholder {
      display: grid;
      gap: .75rem;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      min-height: 280px;
    }

    .theme-preview-pane {
      align-items: center;
      background: linear-gradient(140deg, color-mix(in srgb, #8b5cf6 16%, var(--color-surface-2)), color-mix(in srgb, #22d3ee 10%, var(--color-surface-2)));
      border: 1px dashed color-mix(in srgb, #8b5cf6 45%, var(--color-border));
      border-radius: .75rem;
      display: flex;
      justify-content: center;
      min-height: 260px;
      overflow: hidden;
      padding: .45rem;
    }

    .theme-preview-pane-empty {
      color: var(--color-muted);
      font-size: .74rem;
      text-transform: uppercase;
      letter-spacing: .04em;
    }

    .theme-preview-background-image {
      border-radius: .55rem;
      height: min(100%, 240px);
      max-width: 100%;
      object-fit: contain;
      width: auto;
    }

    .theme-preview-media-lines {
      display: flex;
      flex-direction: column;
      gap: .55rem;
    }

    .theme-preview-media-line {
      align-items: center;
      background: color-mix(in srgb, var(--color-surface-2) 65%, transparent);
      border: 1px solid var(--color-border);
      border-radius: .6rem;
      color: var(--color-text);
      display: flex;
      font-size: .76rem;
      justify-content: space-between;
      padding: .5rem .62rem;
    }

    .theme-preview-media-line--stack {
      align-items: flex-start;
      flex-direction: column;
      gap: .35rem;
    }

    .theme-preview-media-line--split {
      display: grid;
      gap: .55rem;
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .theme-preview-media-cell {
      display: flex;
      flex-direction: column;
      gap: .35rem;
    }

    .theme-preview-play-row {
      display: flex;
      flex-wrap: wrap;
      gap: .35rem;
    }

    .theme-play-btn {
      background: color-mix(in srgb, #8b5cf6 10%, transparent);
      border: 1px solid color-mix(in srgb, #8b5cf6 38%, var(--color-border));
      border-radius: 9999px;
      color: var(--color-text);
      cursor: pointer;
      font-size: .7rem;
      font-weight: 600;
      padding: .28rem .62rem;
    }

    .theme-play-btn--audio {
      background: color-mix(in srgb, #14b8a6 12%, transparent);
      border-color: color-mix(in srgb, #14b8a6 45%, var(--color-border));
    }

    .theme-preview-empty {
      color: var(--color-muted);
      font-size: .7rem;
    }

    .theme-preview-projector {
      border: 1px solid var(--color-border);
      border-radius: .75rem;
      display: flex;
      flex-direction: column;
      gap: .45rem;
      padding: .7rem;
    }

    .theme-preview-projector-title {
      color: var(--color-muted);
      font-size: .72rem;
      font-weight: 700;
      letter-spacing: .04em;
      text-transform: uppercase;
    }

    .theme-preview-projector-placeholder {
      align-items: center;
      border: 1px dashed var(--color-border);
      border-radius: .55rem;
      color: var(--color-muted);
      display: flex;
      justify-content: center;
      min-height: 130px;
      overflow: hidden;
      padding: .35rem;
      text-align: center;
    }

    .theme-preview-projector-image {
      border-radius: .45rem;
      height: min(100%, 120px);
      max-width: 100%;
      object-fit: contain;
      width: auto;
    }

    .theme-assignment-message {
      color: var(--color-muted);
      font-size: .72rem;
      margin-top: .5rem;
    }

    .theme-assignment-current {
      color: var(--color-muted);
      font-size: .66rem;
      margin-top: .22rem;
    }

    /* ── Forms Styles ────────────────────────────────── */
    .form-group {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .form-label {
      color: var(--color-text);
      font-weight: 600;
    }

    .form-control {
      background: color-mix(in srgb, var(--color-surface-2) 50%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 0.6rem;
      color: var(--color-text);
      padding: 0.8rem 1.2rem;
      transition: border-color 0.15s;
    }
    .form-control:focus {
      border-color: #8b5cf6;
      outline: none;
    }

    .form-help {
      color: var(--color-muted);
      font-size: 0.75rem;
      line-height: 1.4;
    }

    .sound-option--disabled {
      opacity: 0.6;
      filter: grayscale(0.35);
    }
  `]
})
export class GamePreferencesPageComponent implements OnInit, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly eventService = inject(EventService);
  private readonly organizationService = inject(OrganizationService);
  private readonly gamePreferencesService = inject(GamePreferencesService);
  private readonly gameThemeCatalogService = inject(GameThemeCatalogService);

  private currentOrganizationId: number | null = null;
  private readonly previewUrlMap = new Map<number, string>();
  private themeBackgroundLoadVersion = 0;

  readonly activePreviewAssetId = signal<number | null>(null);
  readonly previewErrorMessage = signal('');
  readonly themeBackgroundPreviewUrls = signal({ background1: '', background2: '' });

  private readonly themeBackgroundPreviewSync = effect(() => {
    this.selectedThemeId();
    this.artifactsById();
    void this.refreshThemeBackgroundPreviews();
  });

  eventOptions: EventDto[] = [];
  selectedEventId = 0;

  private artifactsByKey = signal<Record<string, ArtifactAssetDto>>({});
  private artifactsById = signal<Record<number, ArtifactAssetDto>>({});

  readonly activeSection         = signal<PrefSection>('themes');
  readonly isSiteAdmin = signal(false);
  readonly selectedSoundCategory = signal<SoundCategoryId>('right-answer');
  readonly selectedVideoCategory = signal<VideoCategoryId>('right-answer');
  readonly themeCatalog = signal<GameThemeDto[]>([]);
  readonly selectedThemeId = signal<number | null>(null);
  readonly activeThemeAssignment = signal<EventThemeAssignmentDto | null>(null);
  readonly isSavingThemeAssignment = signal(false);
  readonly themeAssignmentMessage = signal('');
  readonly timerSettings = signal<EventTimerSettingDto | null>(null);
  readonly isSavingTimerSettings = signal(false);
  readonly timerSettingsMessage = signal('');
  podiumTimerEnabled = true;
  podiumTimerSeconds = 4;
  answerTimerEnabled = true;
  answerTimerSeconds = 15;
  themeSearchText = '';
  selectedThemeCategory = 'all';

  readonly soundSelections = signal<Record<SoundCategoryId, string[]>>({
    'right-answer':   [],
    'wrong-answer':   [],
    'end-of-round':   [],
    'next-team-turn': [],
    'next-round':     [],
    'intro':          [],
    'break':          [],
  });

  readonly videoSelections = signal<Record<VideoCategoryId, string[]>>({
    'right-answer':   [],
    'wrong-answer':   [],
    'end-of-round':   [],
    'next-team-turn': [],
    'next-round':     [],
    'intro':          [],
    'break':          [],
  });

  readonly soundCategories: SoundCategory[] = [
    {
      id: 'right-answer',
      label: 'Right Answer',
      description: 'Plays when a team answers correctly.',
      multiSelect: false,
      options: [
        { id: 'fanfare',   label: 'Fanfare'   },
        { id: 'bell-ding', label: 'Bell Ding' },
        { id: 'cheer',     label: 'Cheer'     },
        { id: 'level-up',  label: 'Level Up'  },
      ],
    },
    {
      id: 'wrong-answer',
      label: 'Wrong Answer',
      description: 'Plays when a team answers incorrectly.',
      multiSelect: false,
      options: [
        { id: 'buzzer',       label: 'Buzzer'       },
        { id: 'sad-trombone', label: 'Sad Trombone' },
        { id: 'thud',         label: 'Thud'         },
        { id: 'whomp',        label: 'Whomp'        },
      ],
    },
    {
      id: 'end-of-round',
      label: 'End of Round',
      description: 'Plays when a round concludes.',
      multiSelect: false,
      options: [
        { id: 'drum-roll',   label: 'Drum Roll'   },
        { id: 'applause',    label: 'Applause'    },
        { id: 'air-horn',    label: 'Air Horn'    },
        { id: 'celebration', label: 'Celebration' },
      ],
    },
    {
      id: 'next-team-turn',
      label: 'Next Team Turn',
      description: 'Plays when the active team changes.',
      multiSelect: false,
      options: [
        { id: 'whoosh',     label: 'Whoosh'     },
        { id: 'drum-hit',   label: 'Drum Hit'   },
        { id: 'alert',      label: 'Alert'      },
        { id: 'transition', label: 'Transition' },
      ],
    },
    {
      id: 'next-round',
      label: 'Next Round',
      description: 'Plays when the next round starts.',
      multiSelect: false,
      options: [
        { id: 'next-round-bell',   label: 'Next Round Bell'   },
        { id: 'next-round-rise',   label: 'Next Round Rise'   },
        { id: 'next-round-alert',  label: 'Next Round Alert'  },
        { id: 'next-round-stinger', label: 'Next Round Stinger' },
      ],
    },
    {
      id: 'intro',
      label: 'Intro',
      description: 'Plays when the game begins.',
      multiSelect: false,
      options: [
        { id: 'fanfare',   label: 'Fanfare'   },
        { id: 'drum-beat', label: 'Drum Beat' },
        { id: 'welcome',   label: 'Welcome'   },
        { id: 'countdown', label: 'Countdown' },
      ],
    },
    {
      id: 'break',
      label: 'Break',
      description: 'Plays during a break between rounds.',
      multiSelect: false,
      options: [
        { id: 'lounge',  label: 'Lounge'  },
        { id: 'upbeat',  label: 'Upbeat'  },
        { id: 'jazz',    label: 'Jazz'    },
        { id: 'ambient', label: 'Ambient' },
      ],
    },
  ];

  readonly videoCategories: VideoCategory[] = [
    {
      id: 'right-answer',
      label: 'Right Answer',
      description: 'Plays when a team answers correctly.',
      multiSelect: false,
      options: [
        { id: 'confetti',  label: 'Confetti'  },
        { id: 'flash',     label: 'Flash'     },
        { id: 'star',      label: 'Star'      },
        { id: 'burst',     label: 'Burst'     },
      ],
    },
    {
      id: 'wrong-answer',
      label: 'Wrong Answer',
      description: 'Plays when a team answers incorrectly.',
      multiSelect: false,
      options: [
        { id: 'red-flash', label: 'Red Flash' },
        { id: 'glitch',    label: 'Glitch'    },
        { id: 'shake',     label: 'Shake'     },
        { id: 'fade',      label: 'Fade'      },
      ],
    },
    {
      id: 'end-of-round',
      label: 'End of Round',
      description: 'Plays when a round concludes.',
      multiSelect: false,
      options: [
        { id: 'scoreboard', label: 'Scoreboard' },
        { id: 'spotlight',  label: 'Spotlight'  },
        { id: 'curtain',    label: 'Curtain'    },
        { id: 'transition', label: 'Transition' },
      ],
    },
    {
      id: 'next-team-turn',
      label: 'Next Team Turn',
      description: 'Plays when the active team changes.',
      multiSelect: false,
      options: [
        { id: 'swipe',  label: 'Swipe'  },
        { id: 'zoom',   label: 'Zoom'   },
        { id: 'slide',  label: 'Slide'  },
        { id: 'reveal', label: 'Reveal' },
      ],
    },
    {
      id: 'next-round',
      label: 'Next Round',
      description: 'Plays when the next round starts.',
      multiSelect: false,
      options: [
        { id: 'next-round-slate',   label: 'Next Round Slate'   },
        { id: 'next-round-pulse',   label: 'Next Round Pulse'   },
        { id: 'next-round-focus',   label: 'Next Round Focus'   },
        { id: 'next-round-transition', label: 'Next Round Transition' },
      ],
    },
    {
      id: 'intro',
      label: 'Intro',
      description: 'Plays when the game begins.',
      multiSelect: false,
      options: [
        { id: 'energetic', label: 'Energetic' },
        { id: 'corporate', label: 'Corporate' },
        { id: 'retro',     label: 'Retro'     },
        { id: 'minimal',   label: 'Minimal'   },
      ],
    },
    {
      id: 'break',
      label: 'Break',
      description: 'Plays during a break between rounds.',
      multiSelect: false,
      options: [
        { id: 'abstract',  label: 'Abstract'  },
        { id: 'cityscape', label: 'Cityscape' },
        { id: 'nature',    label: 'Nature'    },
        { id: 'ambient',   label: 'Ambient'   },
      ],
    },
  ];

  ngOnInit(): void {
    this.loadRoleContext();
    this.loadOrganizationContext();
    this.loadEvents();
    this.loadThemeCatalog();
  }

  private async loadRoleContext(): Promise<void> {
    const isSiteAdmin = await this.authService.hasRoleAsync('SiteAdmin');
    this.isSiteAdmin.set(isSiteAdmin);
  }

  ngOnDestroy(): void {
    this.themeBackgroundLoadVersion++;
    this.themeBackgroundPreviewUrls.set({ background1: '', background2: '' });

    for (const url of this.previewUrlMap.values()) {
      URL.revokeObjectURL(url);
    }
    this.previewUrlMap.clear();
  }

  themeBackgroundPreviewUrl(slot: 1 | 2): string {
    const urls = this.themeBackgroundPreviewUrls();
    return slot === 1 ? urls.background1 : urls.background2;
  }

  private loadOrganizationContext(): void {
    this.organizationService.getMyOrganization().subscribe({
      next: (org) => {
        this.currentOrganizationId = org.id;
      },
      error: (err) => {
        console.error('Failed to load current organization:', err);
      }
    });
  }

  private async refreshThemeBackgroundPreviews(): Promise<void> {
    const requestVersion = ++this.themeBackgroundLoadVersion;

    const primaryAsset = this.getThemeBackgroundAsset(1);
    const projectorAsset = this.getThemeBackgroundAsset(2);

    if (!primaryAsset && !projectorAsset) {
      this.themeBackgroundPreviewUrls.set({ background1: '', background2: '' });
      return;
    }

    const [background1, background2] = await Promise.all([
      this.ensureBackgroundPreviewUrl(primaryAsset),
      this.ensureBackgroundPreviewUrl(projectorAsset)
    ]);

    if (requestVersion !== this.themeBackgroundLoadVersion) {
      return;
    }

    this.themeBackgroundPreviewUrls.set({ background1, background2 });
  }

  private getThemeBackgroundAsset(slot: 1 | 2): ArtifactAssetDto | null {
    const preview = this.selectedThemePreview;
    if (!preview) {
      return null;
    }

    const candidates = preview.artifactReferences
      .filter(reference => this.normalizeThemeRole(reference.mediaRole).includes('background'))
      .sort((a, b) => (a.sequenceOrder - b.sequenceOrder) || (a.id - b.id))
      .map(reference => ({
        sequenceOrder: reference.sequenceOrder,
        asset: this.artifactsById()[reference.artifactAssetId]
      }))
      .filter((entry): entry is { sequenceOrder: number; asset: ArtifactAssetDto } => !!entry.asset && entry.asset.isEnabled);

    if (candidates.length === 0) {
      return null;
    }

    if (slot === 1) {
      return candidates.find(entry => entry.sequenceOrder === 1)?.asset ?? candidates[0].asset;
    }

    const fromSequence = candidates.find(entry => entry.sequenceOrder === 2)?.asset;
    if (fromSequence) {
      return fromSequence;
    }

    return candidates.length > 1 ? candidates[1].asset : null;
  }

  private async ensureBackgroundPreviewUrl(asset: ArtifactAssetDto | null): Promise<string> {
    if (!asset) {
      return '';
    }

    const existing = this.previewUrlMap.get(asset.id);
    if (existing) {
      return existing;
    }

    try {
      const blob = await firstValueFrom(this.gamePreferencesService.getArtifactContent(asset.id));
      const mediaType = this.resolveImageMediaType(blob, asset);
      const url = this.createPlayableBlobUrl(blob, mediaType);
      this.previewUrlMap.set(asset.id, url);
      return url;
    } catch {
      return '';
    }
  }

  onEventChanged(): void {
    if (!this.selectedEventId) {
      this.clearSelections();
      this.activeThemeAssignment.set(null);
      this.themeAssignmentMessage.set('');
      this.timerSettings.set(null);
      this.timerSettingsMessage.set('');
      this.resetTimerFormDefaults();
      return;
    }

    const selectedEventOrganizationId = this.getSelectedEventOrganizationId();
    if (selectedEventOrganizationId && this.currentOrganizationId !== selectedEventOrganizationId) {
      this.currentOrganizationId = selectedEventOrganizationId;
      this.bootstrapArtifactsAndAssignments();
      this.loadThemeAssignmentForEvent(this.selectedEventId);
      this.loadTimerSettingsForEvent(this.selectedEventId);
      return;
    }

    this.loadThemeAssignmentForEvent(this.selectedEventId);
    this.loadTimerSettingsForEvent(this.selectedEventId);
  }

  private loadEvents(): void {
    this.eventService.getAll().subscribe({
      next: (events) => {
        this.eventOptions = events;
        if (!this.currentOrganizationId && events.length > 0) {
          this.currentOrganizationId = events[0].organizationId;
        }

        if (events.length > 0) {
          this.selectedEventId = events[0].id;
          this.currentOrganizationId = events[0].organizationId;
          this.bootstrapArtifactsAndAssignments();
          this.loadThemeAssignmentForEvent(this.selectedEventId);
          this.loadTimerSettingsForEvent(this.selectedEventId);
        }
      },
      error: (err) => {
        console.error('Failed to load events:', err);
        this.eventOptions = [];
      }
    });
  }

  private bootstrapArtifactsAndAssignments(): void {
    const organizationId = this.currentOrganizationId;
    if (!organizationId || organizationId <= 0) {
      console.warn('Organization context is missing for loading game preference artifacts.');
      return;
    }

    this.gamePreferencesService.getArtifactsByOrganization(organizationId).subscribe({
      next: (assets) => {
        const enabledAssets = assets.filter(a => a.isEnabled && a.artifactScope?.toLowerCase() === 'base');
        const map: Record<string, ArtifactAssetDto> = {};
        const byId: Record<number, ArtifactAssetDto> = {};
        for (const a of enabledAssets) {
          map[this.getAssetKey(a.artifactType, a.blobPath)] = a;
          byId[a.id] = a;
        }
        this.artifactsByKey.set(map);
        this.artifactsById.set(byId);
      },
      error: (err) => console.error('Failed to load artifacts:', err)
    });
  }

  private ensureStaticArtifactsExist(): void {
    const orgId = this.currentOrganizationId;
    if (!orgId || orgId <= 0) {
      console.warn('Organization context is missing for creating game preference artifacts.');
      return;
    }
    const createIfMissing = (type: 'Sound' | 'Video', categoryId: string, optionId: string, optionLabel: string) => {
      const blobPath = `library/${type.toLowerCase()}/${categoryId}/${optionId}`;
      const key = this.getAssetKey(type, blobPath);
      if (this.artifactsByKey()[key]) return;

      this.gamePreferencesService.createArtifactMetadata({
        organizationId: orgId,
        artifactType: type,
        artifactCategory: categoryId,
        artifactScope: 'Base',
        isEnabled: true,
        fileName: optionLabel,
        blobPath,
        contentType: '',
        sizeBytes: 0
      }).subscribe({
        next: (created) => {
          this.artifactsByKey.update(current => ({
            ...current,
            [this.getAssetKey(created.artifactType, created.blobPath)]: created
          }));
          this.artifactsById.update(current => ({
            ...current,
            [created.id]: created
          }));
        },
        error: (err) => console.error('Failed to create artifact metadata:', err)
      });
    };

    this.soundCategories.forEach(category =>
      category.options.forEach(option => createIfMissing('Sound', category.id, option.id, option.label))
    );
    this.videoCategories.forEach(category =>
      category.options.forEach(option => createIfMissing('Video', category.id, option.id, option.label))
    );
  }

  private buildDefaultSoundSelections(): Record<SoundCategoryId, string[]> {
    const defaults: Record<SoundCategoryId, string[]> = {
      'right-answer': [], 'wrong-answer': [], 'end-of-round': [], 'next-team-turn': [], 'next-round': [], 'intro': [], 'break': []
    };

    for (const category of this.soundCategories) {
      const selected = this.resolveDefaultOptionId('sound', category.id);
      if (selected) {
        defaults[category.id] = [selected];
      }
    }

    return defaults;
  }

  private buildDefaultVideoSelections(): Record<VideoCategoryId, string[]> {
    const defaults: Record<VideoCategoryId, string[]> = {
      'right-answer': [], 'wrong-answer': [], 'end-of-round': [], 'next-team-turn': [], 'next-round': [], 'intro': [], 'break': []
    };

    for (const category of this.videoCategories) {
      const selected = this.resolveDefaultOptionId('video', category.id);
      if (selected) {
        defaults[category.id] = [selected];
      }
    }

    return defaults;
  }

  private resolveDefaultOptionId(type: 'sound' | 'video', categoryId: string): string | null {
    const candidates = Object.values(this.artifactsById())
      .filter(a => a.artifactType.toLowerCase() === type
        && a.artifactCategory === categoryId
        && a.isEnabled)
      .sort((a, b) => {
        const byDefault = Number(b.default === true) - Number(a.default === true);
        if (byDefault !== 0) return byDefault;
        return new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime();
      });

    const top = candidates[0];
    return top ? `asset:${top.id}` : null;
  }

  private clearSelections(): void {
    this.soundSelections.set({
      'right-answer': [], 'wrong-answer': [], 'end-of-round': [], 'next-team-turn': [], 'next-round': [], 'intro': [], 'break': []
    });
    this.videoSelections.set({
      'right-answer': [], 'wrong-answer': [], 'end-of-round': [], 'next-team-turn': [], 'next-round': [], 'intro': [], 'break': []
    });
    this.activeThemeAssignment.set(null);
    this.selectedThemeId.set(null);
    this.timerSettings.set(null);
    this.resetTimerFormDefaults();
  }

  private resetTimerFormDefaults(): void {
    this.podiumTimerEnabled = true;
    this.podiumTimerSeconds = 4;
    this.answerTimerEnabled = true;
    this.answerTimerSeconds = 15;
  }

  private applyTimerSettingsForm(setting: EventTimerSettingDto): void {
    this.podiumTimerEnabled = setting.podiumTimerEnabled;
    this.podiumTimerSeconds = setting.podiumTimerSeconds;
    this.answerTimerEnabled = setting.answerTimerEnabled;
    this.answerTimerSeconds = setting.answerTimerSeconds;
  }

  private loadTimerSettingsForEvent(eventId: number): void {
    if (!eventId) {
      this.timerSettings.set(null);
      this.resetTimerFormDefaults();
      return;
    }

    this.gamePreferencesService.getTimerSettingsByEvent(eventId).subscribe({
      next: (setting) => {
        this.timerSettings.set(setting);
        this.timerSettingsMessage.set('');

        if (!setting) {
          this.resetTimerFormDefaults();
          return;
        }

        this.applyTimerSettingsForm(setting);
      },
      error: (err) => {
        console.error('Failed to load timer settings:', err);
        this.timerSettings.set(null);
        this.resetTimerFormDefaults();
      }
    });
  }

  saveTimerSettings(): void {
    if (!this.selectedEventId) {
      this.timerSettingsMessage.set('Select an event before saving timer settings.');
      return;
    }

    if (this.podiumTimerSeconds < 1 || this.answerTimerSeconds < 1) {
      this.timerSettingsMessage.set('Timer values must be at least 1 second.');
      return;
    }

    const organizationId = this.getSelectedEventOrganizationId() ?? this.currentOrganizationId;
    if (!organizationId || organizationId <= 0) {
      this.timerSettingsMessage.set('Organization scope could not be resolved for this event.');
      return;
    }

    this.isSavingTimerSettings.set(true);
    this.timerSettingsMessage.set('');

    this.gamePreferencesService.upsertTimerSettings({
      organizationId,
      eventId: this.selectedEventId,
      podiumTimerEnabled: this.podiumTimerEnabled,
      podiumTimerSeconds: this.podiumTimerSeconds,
      answerTimerEnabled: this.answerTimerEnabled,
      answerTimerSeconds: this.answerTimerSeconds
    }).subscribe({
      next: (saved) => {
        this.timerSettings.set(saved);
        this.applyTimerSettingsForm(saved);
        this.timerSettingsMessage.set('Timer settings saved for this event.');
        this.isSavingTimerSettings.set(false);
      },
      error: (err) => {
        console.error('Failed to save timer settings:', err);
        this.timerSettingsMessage.set('Unable to save timer settings.');
        this.isSavingTimerSettings.set(false);
      }
    });
  }

  private getAssetKey(type: string, blobPath: string): string {
    return `${type.toLowerCase()}|${blobPath.toLowerCase()}`;
  }

  private findArtifactId(type: 'Sound' | 'Video', optionId: string): number | null {
    if (optionId.startsWith('asset:')) {
      const parsed = Number(optionId.replace('asset:', ''));
      return Number.isFinite(parsed) ? parsed : null;
    }

    const categoryId = type === 'Sound' ? this.selectedSoundCategory() : this.selectedVideoCategory();
    const blobPath = `library/${type.toLowerCase()}/${categoryId}/${optionId}`;
    const asset = this.artifactsByKey()[this.getAssetKey(type, blobPath)];
    return asset?.id ?? null;
  }

  toggleSound(catId: SoundCategoryId, soundId: string): void {
    if (!this.selectedEventId) return;
    if (this.isUploadedOptionDisabled(soundId)) return;

    const currentlySelected = this.soundSelections()[catId][0];
    if (currentlySelected === soundId) {
      this.soundSelections.update(sel => ({ ...sel, [catId]: [] }));
      return;
    }

    this.selectLocalMedia('sounds', catId, soundId);
  }

  toggleVideo(catId: VideoCategoryId, optId: string): void {
    if (!this.selectedEventId) return;
    if (this.isUploadedOptionDisabled(optId)) return;

    const currentlySelected = this.videoSelections()[catId][0];
    if (currentlySelected === optId) {
      this.videoSelections.update(sel => ({ ...sel, [catId]: [] }));
      return;
    }

    this.selectLocalMedia('video', catId, optId);
  }

  get activeSoundCategory(): SoundCategory {
    return this.soundCategories.find(c => c.id === this.selectedSoundCategory())!;
  }

  isSoundSelected(catId: SoundCategoryId, soundId: string): boolean {
    return this.soundSelections()[catId].includes(soundId);
  }

  getSelectedCount(catId: SoundCategoryId): number {
    return this.soundSelections()[catId].length;
  }

  get activeVideoCategory(): VideoCategory {
    return this.videoCategories.find(c => c.id === this.selectedVideoCategory())!;
  }

  isVideoSelected(catId: VideoCategoryId, optId: string): boolean {
    return this.videoSelections()[catId].includes(optId);
  }

  get themeCategoryOptions(): string[] {
    return [...new Set(this.themeCatalog().map(t => (t.category || 'Uncategorized').trim()))]
      .filter(c => c.length > 0)
      .sort((a, b) => a.localeCompare(b));
  }

  get filteredThemeGroups(): Array<{ category: string; themes: GameThemeDto[] }> {
    const search = this.themeSearchText.trim().toLowerCase();
    const categoryFilter = this.selectedThemeCategory;

    const filtered = this.themeCatalog()
      .filter(t => t.isEnabled && !t.isDeleted)
      .filter(t => categoryFilter === 'all' || t.category === categoryFilter)
      .filter(t => {
        if (!search) return true;

        return t.name.toLowerCase().includes(search)
          || t.themeKey.toLowerCase().includes(search)
          || (t.description ?? '').toLowerCase().includes(search)
          || t.category.toLowerCase().includes(search);
      });

    const grouped = filtered.reduce((acc, theme) => {
      const category = (theme.category || 'Uncategorized').trim() || 'Uncategorized';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(theme);
      return acc;
    }, {} as Record<string, GameThemeDto[]>);

    return Object.keys(grouped)
      .sort((a, b) => a.localeCompare(b))
      .map(category => ({
        category,
        themes: grouped[category]
          .slice()
          .sort((a, b) => this.compareThemesByCreatedOrder(a, b))
      }));
  }

  get selectedThemePreview(): GameThemeDto | null {
    const themeId = this.selectedThemeId();
    if (!themeId) {
      return null;
    }

    return this.themeCatalog().find(t => t.id === themeId) ?? null;
  }

  selectTheme(themeId: number): void {
    this.selectedThemeId.set(themeId);
    this.themeAssignmentMessage.set('');
  }

  saveSelectedThemeAssignment(): void {
    if (!this.selectedEventId) {
      this.themeAssignmentMessage.set('Select an event before saving a theme.');
      return;
    }

    const themeId = this.selectedThemeId();
    if (!themeId) {
      this.themeAssignmentMessage.set('Select a theme before saving.');
      return;
    }

    const organizationId = this.getSelectedEventOrganizationId() ?? this.currentOrganizationId;
    if (!organizationId || organizationId <= 0) {
      this.themeAssignmentMessage.set('Organization scope could not be resolved for this event.');
      return;
    }

    this.isSavingThemeAssignment.set(true);
    this.themeAssignmentMessage.set('');

    this.gameThemeCatalogService.upsertAssignment({
      organizationId,
      eventId: this.selectedEventId,
      gameThemeId: themeId
    }).subscribe({
      next: assignment => {
        this.activeThemeAssignment.set(assignment);
        this.selectedThemeId.set(assignment.gameThemeId);
        this.themeAssignmentMessage.set('Theme assignment saved for this event.');
        this.isSavingThemeAssignment.set(false);
      },
      error: (err) => {
        console.error('Failed to save theme assignment:', err);
        this.themeAssignmentMessage.set('Unable to save theme assignment.');
        this.isSavingThemeAssignment.set(false);
      }
    });
  }

  private loadThemeCatalog(): void {
    this.gameThemeCatalogService.getAll().subscribe({
      next: themes => {
        const ordered = themes
          .filter(t => !t.isDeleted)
          .sort((a, b) => this.compareThemesByCreatedOrder(a, b));

        this.themeCatalog.set(ordered);

        const selectedThemeId = this.selectedThemeId();
        if (selectedThemeId && ordered.some(t => t.id === selectedThemeId)) {
          return;
        }

        this.selectedThemeId.set(ordered.length > 0 ? ordered[0].id : null);
      },
      error: err => {
        console.error('Failed to load game themes:', err);
        this.themeCatalog.set([]);
        this.selectedThemeId.set(null);
      }
    });
  }

  private compareThemesByCreatedOrder(a: GameThemeDto, b: GameThemeDto): number {
    const createdA = Date.parse(a.dateCreated);
    const createdB = Date.parse(b.dateCreated);

    if (!Number.isNaN(createdA) && !Number.isNaN(createdB) && createdA !== createdB) {
      return createdA - createdB;
    }

    if (a.id !== b.id) {
      return a.id - b.id;
    }

    return a.name.localeCompare(b.name);
  }

  private loadThemeAssignmentForEvent(eventId: number): void {
    if (!eventId) {
      this.activeThemeAssignment.set(null);
      return;
    }

    this.gameThemeCatalogService.getAssignmentByEvent(eventId).subscribe({
      next: assignment => {
        this.activeThemeAssignment.set(assignment);

        if (assignment?.gameThemeId) {
          this.selectedThemeId.set(assignment.gameThemeId);
          return;
        }

        const firstTheme = this.themeCatalog().find(t => t.isEnabled && !t.isDeleted);
        this.selectedThemeId.set(firstTheme?.id ?? null);
      },
      error: (err) => {
        console.error('Failed to load event theme assignment:', err);
        this.activeThemeAssignment.set(null);
      }
    });
  }

  getThemePreviewAssets(role: ThemePreviewRole, maxCount: number): ArtifactAssetDto[] {
    const preview = this.selectedThemePreview;
    if (!preview) {
      return [];
    }

    const matchingIds = preview.artifactReferences
      .filter(reference => this.matchesThemePreviewRole(reference.mediaRole, role))
      .sort((a, b) => (a.sequenceOrder - b.sequenceOrder) || (a.id - b.id))
      .map(reference => reference.artifactAssetId)
      .slice(0, maxCount);

    return matchingIds
      .map(assetId => this.artifactsById()[assetId])
      .filter((asset): asset is ArtifactAssetDto => !!asset && asset.isEnabled);
  }

  async toggleThemeAssetPreview(assetId: number, event: Event): Promise<void> {
    event.stopPropagation();

    const asset = this.artifactsById()[assetId];
    if (!asset) {
      this.previewErrorMessage.set('Media asset metadata is not available for this organization.');
      return;
    }

    if (this.activePreviewAssetId() === asset.id) {
      this.activePreviewAssetId.set(null);
      this.previewErrorMessage.set('');
      return;
    }

    this.previewErrorMessage.set('');

    let url = this.previewUrlMap.get(asset.id);
    if (!url) {
      try {
        const blob = await firstValueFrom(this.gamePreferencesService.getArtifactContent(asset.id));
        const mediaType = this.resolveMediaType(blob, asset);
        if (asset.artifactType.toLowerCase() === 'video' && !this.canAttemptVideoPlayback(mediaType, asset.fileName)) {
          this.activePreviewAssetId.set(asset.id);
          this.previewErrorMessage.set(`This browser cannot decode '${asset.fileName}' (${mediaType || 'unknown codec'}). Try MP4 (H.264/AAC).`);
          return;
        }

        url = this.createPlayableBlobUrl(blob, mediaType);
        this.previewUrlMap.set(asset.id, url);
      } catch {
        this.activePreviewAssetId.set(asset.id);
        this.previewErrorMessage.set('Unable to load theme media preview.');
        return;
      }
    }

    this.activePreviewAssetId.set(asset.id);
  }

  isThemeAssetPlaying(assetId: number): boolean {
    return this.activePreviewAssetId() === assetId;
  }

  previewUrlByAssetId(assetId: number): string {
    return this.previewUrlMap.get(assetId) ?? '';
  }

  previewUrlMapHas(assetId: number): boolean {
    return this.previewUrlMap.has(assetId);
  }

  onThemeMediaDecodeError(event: Event): void {
    const media = event.target as HTMLMediaElement | null;
    const code = media?.error?.code;

    let detail = 'Unsupported format or codec.';
    if (code === MediaError.MEDIA_ERR_DECODE) {
      detail = 'Decode failed. The browser cannot decode this file.';
    } else if (code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
      detail = 'Source format is not supported by this browser.';
    }

    this.previewErrorMessage.set(`Unable to play the selected theme media. ${detail}`);
  }

  private matchesThemePreviewRole(mediaRole: string, expected: ThemePreviewRole): boolean {
    const normalizedRole = this.normalizeThemeRole(mediaRole);
    const aliases = this.getThemeRoleAliases(expected);
    return aliases.some(alias => normalizedRole.includes(alias));
  }

  private getThemeRoleAliases(role: ThemePreviewRole): string[] {
    switch (role) {
      case 'right-answer':
        return ['rightanswer', 'correctanswer'];
      case 'wrong-answer':
        return ['wronganswer', 'incorrectanswer'];
      case 'end-of-round':
        return ['endofround', 'roundend'];
      case 'next-round':
        return ['nextround', 'roundnext'];
      case 'end-of-game':
        return ['endofgame', 'gameend'];
      case 'next-team-turn':
        return ['nextteamturn', 'teamturnnext'];
      case 'buzzer':
        return ['buzzer', 'buzz'];
      case 'intro':
        return ['intro', 'introduction'];
      case 'break':
        return ['break', 'intermission'];
      case 'sudden-death':
        return ['suddendeath'];
      case 'three-three-and-out':
        return ['threethreeandout'];
      case 'three-three-sudden-death':
        return ['threethreesuddendeath'];
      case 'head-to-head':
        return ['headtohead'];
      case 'penalty-shot':
        return ['penaltyshot'];
      default:
        return [];
    }
  }

  private normalizeThemeRole(role: string): string {
    return (role ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private resolveImageMediaType(blob: Blob, asset: ArtifactAssetDto): string {
    const blobType = (blob.type ?? '').trim().toLowerCase();
    if (blobType && blobType !== 'application/octet-stream') {
      return blobType;
    }

    const assetType = (asset.contentType ?? '').trim().toLowerCase();
    if (assetType && assetType !== 'application/octet-stream') {
      return assetType;
    }

    const lowerName = (asset.fileName ?? '').toLowerCase();
    if (lowerName.endsWith('.svg')) {
      return 'image/svg+xml';
    }
    if (lowerName.endsWith('.jpg') || lowerName.endsWith('.jpeg')) {
      return 'image/jpeg';
    }

    return 'image/png';
  }

  getSoundOptions(category: SoundCategory): SoundOption[] {
    return Object.values(this.artifactsById())
      .filter(a => a.artifactScope?.toLowerCase() === 'base')
      .filter(a => a.artifactType.toLowerCase() === 'sound' && a.artifactCategory === category.id && a.isEnabled)
      .sort((a, b) => a.fileName.localeCompare(b.fileName))
      .map(a => ({ id: `asset:${a.id}`, label: a.fileName } as SoundOption));
  }

  getVideoOptions(category: VideoCategory): VideoOption[] {
    return Object.values(this.artifactsById())
      .filter(a => a.artifactScope?.toLowerCase() === 'base')
      .filter(a => a.artifactType.toLowerCase() === 'video' && a.artifactCategory === category.id && a.isEnabled)
      .sort((a, b) => a.fileName.localeCompare(b.fileName))
      .map(a => ({ id: `asset:${a.id}`, label: a.fileName } as VideoOption));
  }

  isUploadedOption(optionId: string): boolean {
    return this.parseAssetId(optionId) !== null;
  }

  isUploadedOptionDisabled(optionId: string): boolean {
    const assetId = this.parseAssetId(optionId);
    if (!assetId) return false;
    const asset = this.artifactsById()[assetId];
    return asset ? !asset.isEnabled : false;
  }

  getOptionAsset(optionId: string): ArtifactAssetDto | null {
    const assetId = this.parseAssetId(optionId);
    if (!assetId) return null;
    return this.artifactsById()[assetId] ?? null;
  }

  isOptionDefault(optionId: string): boolean {
    return this.getOptionAsset(optionId)?.default === true;
  }

  isOptionPlaying(optionId: string): boolean {
    const assetId = this.parseAssetId(optionId);
    return !!assetId && this.activePreviewAssetId() === assetId;
  }

  previewUrl(optionId: string): string {
    const assetId = this.parseAssetId(optionId);
    if (!assetId) return '';
    return this.previewUrlMap.get(assetId) ?? '';
  }

  async toggleOptionPreview(optionId: string, event: Event): Promise<void> {
    event.stopPropagation();

    const asset = this.getOptionAsset(optionId);
    if (!asset) return;

    if (this.activePreviewAssetId() === asset.id) {
      this.activePreviewAssetId.set(null);
      this.previewErrorMessage.set('');
      return;
    }

    this.previewErrorMessage.set('');

    let url = this.previewUrlMap.get(asset.id);
    if (!url) {
      try {
        const blob = await firstValueFrom(this.gamePreferencesService.getArtifactContent(asset.id));
        const mediaType = this.resolveMediaType(blob, asset);
        if (asset.artifactType.toLowerCase() === 'video' && !this.canAttemptVideoPlayback(mediaType, asset.fileName)) {
          this.activePreviewAssetId.set(asset.id);
          this.previewErrorMessage.set(`This browser cannot decode '${asset.fileName}' (${mediaType || 'unknown codec'}). Try MP4 (H.264/AAC).`);
          return;
        }

        url = this.createPlayableBlobUrl(blob, mediaType);
        this.previewUrlMap.set(asset.id, url);
      } catch {
        this.activePreviewAssetId.set(asset.id);
        this.previewErrorMessage.set('Unable to load media preview.');
        return;
      }
    }

    this.activePreviewAssetId.set(asset.id);
  }

  clearMediaDecodeError(): void {
    if (this.previewErrorMessage()) {
      this.previewErrorMessage.set('');
    }
  }

  onOptionMediaDecodeError(event: Event, optionId: string): void {
    const asset = this.getOptionAsset(optionId);
    if (!asset) return;

    const media = event.target as HTMLMediaElement | null;
    const code = media?.error?.code;

    let detail = 'Unsupported format or codec.';
    if (code === MediaError.MEDIA_ERR_DECODE) {
      detail = 'Decode failed. The browser cannot decode this file.';
    } else if (code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
      detail = 'Source format is not supported by this browser.';
    }

    this.previewErrorMessage.set(`Unable to play '${asset.fileName}'. ${detail}`);
  }

  formatAssetSize(sizeBytes: number): string {
    if (!sizeBytes) return '0 B';
    if (sizeBytes >= 1024 * 1024) return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
    if (sizeBytes >= 1024) return `${Math.round(sizeBytes / 1024)} KB`;
    return `${sizeBytes} B`;
  }

  toggleAssetEnabled(optionId: string): void {
    const assetId = this.parseAssetId(optionId);
    if (!assetId) return;

    const asset = this.artifactsById()[assetId];
    if (!asset) return;

    this.gamePreferencesService.setArtifactEnabled(assetId, !asset.isEnabled).subscribe({
      next: (updated) => {
        this.artifactsById.update(current => ({ ...current, [updated.id]: updated }));
      },
      error: (err) => console.error('Failed to toggle artifact enabled state:', err)
    });
  }

  private parseAssetId(optionId: string): number | null {
    if (!optionId.startsWith('asset:')) return null;
    const parsed = Number(optionId.replace('asset:', ''));
    return Number.isFinite(parsed) ? parsed : null;
  }

  private createPlayableBlobUrl(blob: Blob, mediaType: string): string {
    if (!mediaType) {
      return URL.createObjectURL(blob);
    }

    const typedBlob = new Blob([blob], { type: mediaType });
    return URL.createObjectURL(typedBlob);
  }

  private resolveMediaType(blob: Blob, asset: ArtifactAssetDto): string {
    const fallbackType = asset.artifactType === 'Sound'
      ? this.resolveAudioContentType(asset.fileName)
      : this.resolveVideoContentType(asset.fileName);
    const normalizedBlobType = (blob.type ?? '').trim().toLowerCase();
    const normalizedAssetType = (asset.contentType ?? '').trim().toLowerCase();
    const isVideo = asset.artifactType.toLowerCase() === 'video';

    if (normalizedBlobType && normalizedBlobType !== 'application/octet-stream') {
      return isVideo
        ? this.normalizeVideoMediaType(normalizedBlobType, asset.fileName)
        : this.normalizeAudioMediaType(normalizedBlobType, asset.fileName);
    }

    const resolved = normalizedAssetType || fallbackType;
    return isVideo
      ? this.normalizeVideoMediaType(resolved, asset.fileName)
      : this.normalizeAudioMediaType(resolved, asset.fileName);
  }

  private resolveAudioContentType(fileName: string): string {
    const lower = (fileName ?? '').toLowerCase();
    if (lower.endsWith('.m4a')) {
      return 'audio/mp4';
    }
    if (lower.endsWith('.wav')) {
      return 'audio/wav';
    }
    if (lower.endsWith('.ogg')) {
      return 'audio/ogg';
    }
    return 'audio/mpeg';
  }

  private normalizeAudioMediaType(mediaType: string, fileName: string): string {
    const type = (mediaType ?? '').toLowerCase().trim();
    const lowerFile = (fileName ?? '').toLowerCase();

    if (!type || type === 'application/octet-stream') {
      return this.resolveAudioContentType(fileName);
    }

    if (lowerFile.endsWith('.m4a')
      || type === 'audio/x-m4a'
      || type === 'audio/m4a'
      || type === 'audio/mp4') {
      return 'audio/mp4';
    }

    return type;
  }

  private canAttemptVideoPlayback(mediaType: string, fileName: string): boolean {
    const lowerName = (fileName ?? '').toLowerCase();
    if (lowerName.endsWith('.wmv')) {
      return true;
    }

    return this.canBrowserPlayVideoType(mediaType);
  }

  private canBrowserPlayVideoType(mediaType: string): boolean {
    if (!mediaType) {
      return false;
    }

    const video = document.createElement('video');
    return video.canPlayType(mediaType) !== '';
  }

  private resolveVideoContentType(fileName: string): string {
    const lower = (fileName ?? '').toLowerCase();
    if (lower.endsWith('.wmv')) {
      return 'video/x-ms-wmv';
    }
    if (lower.endsWith('.mov')) {
      return 'video/quicktime';
    }
    return 'video/mp4';
  }

  private normalizeVideoMediaType(mediaType: string, fileName: string): string {
    const type = (mediaType ?? '').toLowerCase().trim();
    const lowerFile = (fileName ?? '').toLowerCase();

    if (!type || type === 'application/octet-stream') {
      return this.resolveVideoContentType(fileName);
    }

    if (lowerFile.endsWith('.wmv') || type === 'application/x-ms-wmv' || type === 'video/wmv') {
      return 'video/x-ms-wmv';
    }

    if (lowerFile.endsWith('.mov') || type === 'video/mov' || type.includes('quicktime')) {
      return 'video/quicktime';
    }

    if (lowerFile.endsWith('.mp4') || type.includes('mp4')) {
      return 'video/mp4';
    }

    return type;
  }

  private selectLocalMedia(
    section: 'sounds' | 'video',
    category: string,
    optionId: string
  ): void {
    if (section === 'sounds') {
      this.soundSelections.update(sel => ({ ...sel, [category as SoundCategoryId]: [optionId] }));
    } else {
      this.videoSelections.update(sel => ({ ...sel, [category as VideoCategoryId]: [optionId] }));
    }
  }

  private getSelectedEventOrganizationId(): number | null {
    if (!this.selectedEventId) {
      return null;
    }

    const selectedEvent = this.eventOptions.find(e => e.id === this.selectedEventId);
    return selectedEvent?.organizationId ?? null;
  }

}
